import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const categoryIcons: Record<string, string> = {
  'Shoes': 'ri-footprint-line',
  'Hoodies/Sweaters': 'ri-t-shirt-line',
  'T-Shirts': 'ri-shirt-line',
  'Jackets': 'ri-t-shirt-air-line',
  'Pants/Shorts': 'ri-user-line',
  'Headwear': 'ri-user-5-line',
  'Sets': 'ri-stack-line',
  'Underwear/Underpants': 'ri-checkbox-blank-circle-line',
  'Jersey': 'ri-trophy-line',
  'Accessories': 'ri-glasses-line',
  'Bags': 'ri-shopping-bag-line',
  'Belts': 'ri-link',
  'Perfumes': 'ri-drop-line',
  'Other Stuff': 'ri-more-line',
};

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const closeAll = () => {
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex items-center justify-between h-16 md:h-16">
        <Link to="/" className="flex items-center gap-2">
          <span className="font-heading text-xl md:text-2xl font-bold text-foreground-900 tracking-tight">
            Itaobuy <span className="text-primary-500">Spreadsheet</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <Link to="/" className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap">
            Home
          </Link>
          <Link to="/guida-acquisti/" className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap">
            Guida Acquisti
          </Link>
          <Link to="/articolo/" className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap">
            Guida
          </Link>

          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
            >
              Categorie
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {isDropdownOpen && (
              <div className="absolute top-full right-0 mt-2 w-56 bg-background-50 rounded-lg border border-background-200/70 py-2 max-h-[420px] overflow-y-auto">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    <i className={`${categoryIcons[cat.name] || 'ri-price-tag-3-line'} text-foreground-400 text-xs`}></i>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="hidden sm:inline-flex items-center px-4 py-2 rounded-full bg-foreground-900 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            itaobuy sheet
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            buy it now
          </a>

          <button
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Menu"
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-6 py-4 max-h-[80vh] overflow-y-auto">
          <Link to="/" className="flex items-center gap-2 py-3 text-sm font-medium text-foreground-900 hover:text-primary-500 transition-colors" onClick={closeAll}>
            <i className="ri-home-4-line text-foreground-400"></i>
            Home
          </Link>
          <Link to="/guida-acquisti/" className="flex items-center gap-2 py-3 text-sm font-medium text-foreground-900 hover:text-primary-500 transition-colors" onClick={closeAll}>
            <i className="ri-shopping-cart-2-line text-foreground-400"></i>
            Guida Acquisti
          </Link>
          <Link to="/articolo/" className="flex items-center gap-2 py-3 text-sm font-medium text-foreground-900 hover:text-primary-500 transition-colors" onClick={closeAll}>
            <i className="ri-book-open-line text-foreground-400"></i>
            Guida Completa
          </Link>

          <div className="h-px bg-background-200/70 my-3"></div>

          <p className="py-2 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
            Categorie
          </p>
          <div className="grid grid-cols-2 gap-1 mb-3">
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-1.5 py-2.5 text-xs text-foreground-600 hover:text-foreground-950 transition-colors whitespace-nowrap"
                onClick={closeAll}
              >
                <i className={`${categoryIcons[cat.name] || 'ri-price-tag-3-line'} text-foreground-300 text-[10px]`}></i>
                {cat.name}
              </a>
            ))}
          </div>

          <div className="h-px bg-background-200/70 my-3"></div>

          <p className="py-2 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
            Altre Guide
          </p>
          <Link to="/sicurezza/" className="flex items-center gap-2 py-2.5 text-sm text-foreground-600 hover:text-foreground-950 transition-colors" onClick={closeAll}>
            <i className="ri-shield-check-line text-foreground-400"></i>
            Sicurezza
          </Link>
          <Link to="/confronto/" className="flex items-center gap-2 py-2.5 text-sm text-foreground-600 hover:text-foreground-950 transition-colors" onClick={closeAll}>
            <i className="ri-scales-3-line text-foreground-400"></i>
            Confronto
          </Link>
          <Link to="/glossario/" className="flex items-center gap-2 py-2.5 text-sm text-foreground-600 hover:text-foreground-950 transition-colors" onClick={closeAll}>
            <i className="ri-book-2-line text-foreground-400"></i>
            Glossario
          </Link>
        </div>
      )}
    </nav>
  );
}